import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks";
import useCurrency from "./useCurrency";
import { useEffect, useState } from "react";
import { ICartItemProps } from "@/template/blocks/NavigationSection/component/NavAuth/CartControl/cartInterface";
import useChangeAnnualBilling from "@/lib/api/checkout";
import { useUpgradePlan } from "@/lib/api/plan";
import { TBillingInterval } from "@/redux/slices/user/types";
import moment from "moment";
import { deleteCartItem } from "@/redux/slices/user/thunks/deleteCartItem";
import {
  buildDomainDescriptionsAndTip,
  buildItemLabel,
  formatDate,
  getDomainFlags,
  hasVoipUserAddon,
  isVoipAddon,
  makeCheckPrice,
  makeGetAddOnPrice,
} from "../advancedCartItemHelper";

export const useAdvancedCartItemLogic = ({
  item,
  billing_interval,
  reviewOrder,
  setRenew,
  renew,
  orderDetail,
  isPaymentReceipt,
}: ICartItemProps) => {
  const dispatch = useTypedDispatch();
  const currency = useCurrency();

  const plans = useTypedSelector((state) => state.store.plan_prices?.plans);
  const cart = useTypedSelector((state) => state?.user?.cart?.items);
  const user = useTypedSelector((state) => state?.user?.user);
  const planPrice = useTypedSelector((state) => state.store.plan_prices.plans);

  const [checked, setChecked] = useState<TBillingInterval>(billing_interval);
  const [radioLoading, setRadioLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [cartReady, setCartReady] = useState(false);

  const { loading } = item;

  const { isDomain, isDomainTransfer, isDomainBackorder } =
    getDomainFlags(item);
  const isPlanChange = item.type?.name === "VoIP Plan Change";

  const itemName = item.name.replace(/(\d+) x /, "");

  const { descriptions, tip } = buildDomainDescriptionsAndTip({
    item,
    itemName,
    billing_interval,
    currency,
    isDomain,
    isDomainTransfer,
    isDomainBackorder,
  });

  const changePlanOrAddon = useChangeAnnualBilling();
  const upgradePlan = useUpgradePlan();

  /* ---------------- Billing cycle change logic ---------------- */

  const changeBillingCycle = async (val: TBillingInterval) => {
    setChecked(val);
    setRadioLoading(true);

    const isVoipPlanChange = item?.type?.name === "VoIP Plan Change";
    const voipPlanName = isVoipPlanChange
      ? item?.description?.split(" ")[2]
      : null;

    const planId =
      user?.voip_plan === 8
        ? item?.type?.sub_type_id + 1
        : planPrice?.find((s) => s?.name === voipPlanName)?.id;

    if (
      cart.some((c) => c.type?.name === "VoIP Plan Change") ||
      user?.voip_plan === 8
    ) {
      await upgradePlan({
        planId: planId,
        quantity: user?.active_users,
        billing_interval: val,
      }).finally(() => setRadioLoading(false));
    } else {
      await changePlanOrAddon({
        itemType: 13,
        subType: item.type.sub_type_id + 1,
        itemData: {
          billing_interval: val,
        },
      }).finally(() => setRadioLoading(false));
    }
  };

  /* ------------------------ Add-on logic ------------------------ */

  const onAddonSelected = async (value: boolean) => {
    setCheckLoading(true);

    if (cart.some((c) => c.type?.name === "VoIP Addon")) {
      dispatch(
        deleteCartItem(cart.find((c) => c.type?.name === "VoIP Addon")?.id)
      ).then(() => setCheckLoading(false));
    } else if (
      cart?.find((c) => c.type?.name === "VoIP Plan Change") &&
      user?.addons.length > 0
    ) {
      setRenew(value);
      setCheckLoading(false);
    } else {
      await changePlanOrAddon({
        itemType: 22,
        subType: 1,
        itemData: {
          period: 1,
        },
      }).then(() => setCheckLoading(false));
    }
  };

  /* 
    Clean up stray VoIP Addons for unauthenticated users without VoIP.
  */
  useEffect(() => {
    const cartHasItemTypes = (cart, typeNames: string[]) =>
      cart?.some((c) => typeNames.includes(c.type?.name)) ?? false;

    const voipAddon = cart?.find((c) => c.type?.name === "VoIP Addon");
    const hasUserItem = cartHasItemTypes(cart, ["User", "VoIP Plan Change"]);

    const isUnauthenticatedWithoutVoip =
      !user?.authenticated && !user?.has_voip;
    const isPremiumPlan = user?.voip_plan === 11;

    const shouldRemoveVoipAddon =
      !!voipAddon &&
      !hasUserItem &&
      (isUnauthenticatedWithoutVoip || isPremiumPlan);

    if (shouldRemoveVoipAddon) {
      dispatch(deleteCartItem(voipAddon.id)).finally(() => setCartReady(true));
    } else {
      setCartReady(true);
    }
  }, []);

  const hasPlanChangeWithDesc = cart?.some(
    (i) => i?.type?.name === "VoIP Plan Change" && !!i?.description?.trim()
  );

  const hasVoipAddon = cart?.some((i) => i?.type?.name === "VoIP Addon");

  const optimized =
    hasPlanChangeWithDesc &&
    user?.voip_plan === 11 &&
    !hasVoipAddon &&
    (user?.addons?.length ?? 0) === 0;

  /*
    Auto-add addon in specific "optimized" scenarios
  */
  useEffect(() => {
    (async () => {
      if (optimized) {
        setCheckLoading(true);
        await changePlanOrAddon({
          itemType: 22,
          subType: 1,
          itemData: {
            period: 1,
          },
        }).then(() => setCheckLoading(false));
      }
    })();
  }, []);

  /* ----------------------- Price helpers ----------------------- */

  const checkPrice = makeCheckPrice(item, plans, currency);
  const getAddOnPrice = makeGetAddOnPrice(item, plans, currency);

  const checkDateRange = () => {
    const systemDate = moment(user?.renewal_date);
    const currentDate = moment();

    const startRange = systemDate.clone().subtract(31, "days");
    const endRange = systemDate.clone().add(31, "days");

    return !currentDate.isBetween(startRange, endRange, "day", "[]");
  };

  // Add-on presence and derived flags
  const hasUserAddons = user?.addons?.length > 0;

  const orderDetailHasAddon = orderDetail?.items?.some(isVoipAddon);

  const cartHasVoipAddon = !!cart?.some(isVoipAddon);
  const cartOrUserHasAddons =
    (user?.authenticated && hasUserAddons) ||
    cartHasVoipAddon ||
    orderDetailHasAddon;

  const thirdWord = item?.description?.split?.(" ")?.[2] ?? "";
  const isPremium = thirdWord === "Premium";

  const derivedChecked =
    cartOrUserHasAddons || item?.type?.sub_type_id === 10 || isPremium;

  const isPremiumVoipChange =
    item?.type?.name === "VoIP Plan Change" &&
    item?.description?.split(" ")[2] === "Premium";
  const isSpecialSubtype = item?.type?.sub_type_id === 10;

  const isVoIPChange = item?.description?.split(" ")[0];

  const voip = user?.voip;
  const isExistingVoipUser =
    voip?.has_voip_cart_users &&
    voip?.has_voip_customer &&
    !voip?.has_voip_trial;

  const shouldShow =
    isExistingVoipUser ||
    (hasUserAddons && isVoIPChange === "Upgrade") ||
    isPremiumVoipChange ||
    isSpecialSubtype ||
    checkLoading ||
    reviewOrder;

  const isDisabled =
    (shouldShow && !user?.voip?.has_voip_trial) ||
    (hasUserAddons && isVoIPChange === "Upgrade");

  // Controlled checkbox state
  const [checkBoxChecked, setCheckBoxChecked] = useState(
    hasUserAddons ? renew : derivedChecked
  );

  useEffect(() => {
    setCheckBoxChecked(hasUserAddons ? renew : derivedChecked);
    // cart, user affect derivedChecked; keep them in deps
  }, [derivedChecked, cart, user, hasUserAddons, renew]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.checked;
    setCheckBoxChecked(next);
    onAddonSelected(next);
  }

  /* ------------------------ Labels & costs ------------------------ */

  const getItemLabel = () => buildItemLabel(item, plans);

  const hasBothCosts = item?.cost_net && item?.cost_recurring;
  const hasDifferentCosts = item?.cost_net !== item?.cost_recurring;

  const shouldShowCostDifference =
    isDomain && !isDomainBackorder && hasBothCosts && hasDifferentCosts;

  const shouldShowDomainPriceBreakdown =
    isDomain && !isDomainTransfer && !isDomainBackorder && hasDifferentCosts;

  const cartUserHasVoipAddon = hasVoipUserAddon(cart);
  const orderDetailHasVoipAddon = hasVoipUserAddon(orderDetail?.items);
  const userHasVoip = Boolean(user?.has_voip);

  const isFreeItem =
    item?.cost_net === 0 &&
    !isDomainBackorder &&
    !hasUserAddons &&
    !cartUserHasVoipAddon &&
    isPaymentReceipt &&
    !orderDetailHasVoipAddon &&
    !userHasVoip;

  const isUserWithVoipAddon =
    ["User", "VoIP Plan Change"].includes(item?.type?.name) &&
    (cart?.some(isVoipAddon) || orderDetailHasAddon);

  const voipAddonItem =
    cart?.find(isVoipAddon) || orderDetail?.items?.find(isVoipAddon);
  const totalCost = item?.cost_net + (voipAddonItem?.cost_net || 0);

  const getDisplayPrice = () => {
    if (isFreeItem) return "FREE";

    if (isUserWithVoipAddon) {
      return `${currency}${(totalCost / 100).toFixed(2)}`;
    }

    return `${currency}${(item?.cost_net / 100).toFixed(2)}`;
  };

  const getAddOnLabel = () => {
    const isPremiumVoipChangeInner =
      item?.type?.name === "VoIP Plan Change" &&
      item?.description?.split(" ")[2] === "Premium";

    const isPremiumUser = item?.type?.sub_type_id === 10;

    if (isPremiumVoipChangeInner || isPremiumUser) {
      return "included in Premium Plan";
    }

    return getAddOnPrice();
  };

  const getRenewalDate = () => {
    const now = moment();

    const monthly = formatDate(now.clone().add(1, "months"));
    const yearly = formatDate(now.clone().add(1, "years"));
    const renewal = formatDate(user?.renewal_date);

    if (
      user?.has_voip &&
      isVoIPChange !== "Upgrade" &&
      user?.voip_plan !== 8 &&
      checkDateRange()
    ) {
      return renewal;
    }

    if (checked === "monthly") {
      if (!user?.has_voip) return monthly;
      if (user?.voip_plan === 8) return monthly;
      return renewal;
    }

    return yearly;
  };

  const hasVoipButNotPlan8 = user?.has_voip && user?.voip_plan !== 8;
  const isNotVoipPlanChange = item?.type?.name !== "VoIP Plan Change";

  const shouldDisable =
    radioLoading || (hasVoipButNotPlan8 && isNotVoipPlanChange) || reviewOrder;

  /* ------------------------ Return unchanged shape ------------------------ */

  return {
    checked,
    loading,
    totalCost,
    isUserWithVoipAddon,
    isFreeItem,
    shouldShowCostDifference,
    shouldShow,
    cartOrUserHasAddons,
    getAddOnPrice,
    checkPrice,
    onAddonSelected,
    changeBillingCycle,
    tip,
    radioLoading,
    isPlanChange,
    descriptions,
    getItemLabel,
    currency,
    isDomainTransfer,
    isDomainBackorder,
    user,
    shouldShowDomainPriceBreakdown,
    getDisplayPrice,
    getAddOnLabel,
    getRenewalDate,
    shouldDisable,
    cartReady,
    checkDateRange,
    isVoIPChange,
    renew,
    checkBoxChecked,
    isDisabled,
    handleChange,
    checkLoading,
  };
};
